/**
 * CriticalBalancePopup Component
 *
 * A modal popup that appears when users interact with the greyed-out
 * prompt bar while their balance is critically low (<$3 in production mode).
 *
 * Features:
 * - Dynamic "click here" link based on payment method status
 * - Keyboard accessible (Escape to close, Tab trapping)
 * - Dismissable via backdrop click or close button
 * - Smooth animations
 */

import React, { useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import styled, { keyframes } from 'styled-components';
import { BODY_PORTAL_Z_INDEX } from './styles';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';

// ============================================================================
// Types
// ============================================================================

export interface CriticalBalancePopupProps {
  /** Whether the popup is open */
  isOpen: boolean;
  /** Callback to close the popup */
  onClose: () => void;
  /** Whether user has at least one payment method on file */
  hasPaymentMethod: boolean;
  /** Whether payment method status is still loading */
  isLoadingPaymentStatus: boolean;
  /** Callback when user clicks the navigation link */
  onNavigate: (destination: 'add-payment' | 'add-tokens') => void;
  /** Current balance in USD (for display) */
  currentBalanceUsd?: number;
}

// ============================================================================
// Animations
// ============================================================================

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95) translateY(10px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
`;

// ============================================================================
// Styled Components
// ============================================================================

const PopupBackdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  /* Body-portaled: must clear the fullscreen chat (see BODY_PORTAL_Z_INDEX). */
  z-index: ${BODY_PORTAL_Z_INDEX};
  animation: ${fadeIn} 0.2s ease-out;
`;

const PopupCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 28px;
  width: 90%;
  max-width: 360px;
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.15);
  animation: ${scaleIn} 0.25s ease-out;
  text-align: center;
`;

const IconContainer = styled.div`
  width: 56px;
  height: 56px;
  margin: 0 auto 20px;
  background: linear-gradient(135deg, #FEE2E2 0%, #FECACA 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 28px;
    height: 28px;
    color: #DC2626;
  }
`;

const PopupTitle = styled.h3`
  margin: 0 0 12px;
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
`;

const PopupMessage = styled.p`
  margin: 0 0 24px;
  font-size: 0.9375rem;
  color: #6b7280;
  line-height: 1.6;
`;

const ClickHereLink = styled.button`
  background: none;
  border: none;
  color: #DC2626;
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;
  font-size: inherit;
  padding: 0;
  transition: color 0.15s ease;

  &:hover {
    color: #B91C1C;
  }

  &:disabled {
    color: #9CA3AF;
    cursor: wait;
  }
`;

const CloseButton = styled.button`
  margin-top: 8px;
  padding: 12px 28px;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  color: #4b5563;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: #e5e7eb;
  }

  &:focus {
    outline: 2px solid #DC2626;
    outline-offset: 2px;
  }
`;

// ============================================================================
// Wallet Icon SVG
// ============================================================================

const WalletIcon: React.FC = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 12V7H5a2 2 0 0 1 0-4h14v4" />
    <path d="M3 5v14a2 2 0 0 0 2 2h16v-5" />
    <path d="M18 12a2 2 0 0 0 0 4h4v-4Z" />
  </svg>
);

// ============================================================================
// Component
// ============================================================================

export const CriticalBalancePopup: React.FC<CriticalBalancePopupProps> = ({
  isOpen,
  onClose,
  hasPaymentMethod,
  isLoadingPaymentStatus,
  onNavigate,
  currentBalanceUsd,
}) => {
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  // Focus management and keyboard handling
  useEffect(() => {
    if (!isOpen) return;

    // Focus the close button when popup opens
    closeButtonRef.current?.focus();

    // Handle keyboard events
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }

      // Tab trapping
      if (e.key === 'Tab' && popupRef.current) {
        const focusableElements = popupRef.current.querySelectorAll(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        const firstElement = focusableElements[0] as HTMLElement;
        const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

        if (e.shiftKey && document.activeElement === firstElement) {
          e.preventDefault();
          lastElement?.focus();
        } else if (!e.shiftKey && document.activeElement === lastElement) {
          e.preventDefault();
          firstElement?.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  // Prevent body scroll — shared reference-counted lock (2026-08-12
  // scrolling round) so overlapping overlays can't restore each other's
  // snapshots out of order.
  useBodyScrollLock(isOpen);

  // Handle backdrop click
  const handleBackdropClick = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  }, [onClose]);

  // Handle "click here" link click
  const handleClickHere = useCallback(() => {
    onClose();
    onNavigate(hasPaymentMethod ? 'add-tokens' : 'add-payment');
  }, [hasPaymentMethod, onNavigate, onClose]);

  if (!isOpen) return null;

  // Format balance for display
  const balanceText = currentBalanceUsd !== undefined
    ? `$${currentBalanceUsd.toFixed(2)}`
    : 'very low';

  // Dynamic action text based on payment method status. 2026-08-13 WU
  // migration: user-facing copy says "top up pr0" — the routed
  // 'add-tokens' destination identifier below stays EXACTLY as-is
  // (?action=add-tokens deep-link).
  const actionText = isLoadingPaymentStatus
    ? 'loading...'
    : hasPaymentMethod
      ? 'top up pr0'
      : 'add a payment method';

  const popupContent = (
    <PopupBackdrop
      onClick={handleBackdropClick}
      onMouseDown={(e) => e.stopPropagation()}
      data-prest0n-portal="true"
      role="dialog"
      aria-modal="true"
      aria-labelledby="popup-title"
    >
      <PopupCard ref={popupRef}>
        <IconContainer>
          <WalletIcon />
        </IconContainer>
        <PopupTitle id="popup-title">balance too low</PopupTitle>
        <PopupMessage>
          your balance is {balanceText}. to continue chatting, please{' '}
          <ClickHereLink
            onClick={handleClickHere}
            disabled={isLoadingPaymentStatus}
            aria-label={`Click to ${actionText}`}
          >
            click here
          </ClickHereLink>{' '}
          to {actionText}.
        </PopupMessage>
        <CloseButton ref={closeButtonRef} onClick={onClose}>
          got it
        </CloseButton>
      </PopupCard>
    </PopupBackdrop>
  );

  // Render via portal to ensure proper z-index stacking
  return ReactDOM.createPortal(popupContent, document.body);
};

export default CriticalBalancePopup;
